import { Component, computed, DestroyRef, effect, ElementRef, inject, OnInit, PLATFORM_ID, signal, ViewChild } from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { vatCountryFormatValidator } from '../../../core/validators/vatCountryFormat.validator';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TextInputComponent } from "../../../shared-with-client/text-input/text-input.component";
import { CheckoutMode, ICae, ICartItem, ICreateSaleData, InvoiceStatus, InvoicingService, IPaymentMethod, ISaleStatus } from '../../../core/services/invoicing.service';
import { saleLinesHaveNonZeroUnitPrice } from '../../../core/services/invoicing-cart.util';
import { MerchantsService } from '../../../core/services/merchants.service';
import { StoresService } from '../../../core/services/stores.service';
import { ToolsService } from '../../../core/services/tools.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { TabBarComponent } from "../../../shared/tab-bar/tab-bar.component";
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { MultiCheckButtonsComponent } from '../../../shared/multi-check-buttons/multi-check-buttons.component';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { TextareaInputComponent } from '../../../shared-with-client/textarea-input/textarea-input.component';
import { IntlService } from '../../../core/services/intl.service';
import { CountryService } from '../../../core/services/country.service';
import { IconComponent } from '../../../shared-with-client/icon/icon.component';
import { DropdownInputV3Component } from '../../../shared-with-client/dropdown-input-v3/dropdown-input-v3.component';
import { PhoneInputComponent } from '../../../shared-with-client/phone-input/phone-input.component';
import { TeyaService } from '../../../core/services/teya.service';
import { environment } from '../../../../environments/environment';
import { ChangeData } from 'untold-ngx-intl-tel-input';

declare const Stripe: stripe.StripeStatic;

type PaymentChannel = 'register' | 'mbway_request' | 'bukpay' | 'teya';

interface ICaeOption extends ICae {
    label: string;
}

/** Matches buk-ds ChoiceTabs iconTone. */
type ChoiceTabIconTone = 'default' | 'brand' | 'inverse';

interface PaymentChannelTab {
    id: PaymentChannel;
    labelKey: string;
    hintKey: string;
    icon?: string;
    logoSrc?: string;
    logoClass?: string;
    iconTone?: ChoiceTabIconTone;
}

@Component({
  selector: 'app-invoice-payment-method',
  imports: [
    TextInputComponent,
    TranslatePipe,
    ReactiveFormsModule,
    LoaderComponent,
    TabBarComponent,
    MerchantCurrencyPipe,
    MultiCheckButtonsComponent,
    ButtonComponent,
    TextareaInputComponent,
    IconComponent,
    DropdownInputV3Component,
    PhoneInputComponent,
    NgClass,
  ],
  templateUrl: './invoice-payment-method.component.html',
  styleUrl: './invoice-payment-method.component.scss'
})
export class InvoicePaymentMethodComponent implements OnInit {
    @ViewChild('paymentMethodsContainer', { static: false }) paymentMethodsContainer!: ElementRef;

    CheckoutMode = CheckoutMode;
    ISaleStatus = ISaleStatus;
    InvoiceStatus = InvoiceStatus;

    private invoicingService = inject(InvoicingService);
    private intlService = inject(IntlService);
    private countryService = inject(CountryService);
    private translateService = inject(TranslateService);
    private toastNotificationsService = inject(ToastNotificationsService);
    private merchantService = inject(MerchantsService);
    private storesService = inject(StoresService);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private activatedRoute = inject(ActivatedRoute);
    private toolsService = inject(ToolsService);
    private teyaService = inject(TeyaService);
    private platformId = inject(PLATFORM_ID);
    private destroyRef = inject(DestroyRef);
    private stripe: stripe.Stripe | null = null;

    public currentMerchant = toSignal(this.merchantService.activeMerchant);
    public currentStore = toSignal(this.storesService.getActiveStore());

    public form!: FormGroup;

    public invoicingCart = toSignal(this.invoicingService.invoicingCart$);
    public invoicingCartTotals = toSignal(this.invoicingService.invoicingCartTotals$, { initialValue: {} });
    public selectedPatient = toSignal(this.invoicingService.invoicingPatient$, { initialValue: null });
    public checkoutMode = toSignal(this.invoicingService.checkoutMode$, { initialValue: CheckoutMode.CREATE_FULL_SALE });

    public existingSaleData = signal<any>(null);

    public activeTab = signal<'full' | 'partial' | 'later'>('full');
    public paymentChannel = signal<PaymentChannel>('register');

    public clientEditMode = signal(false);

    public PaymentMethod = IPaymentMethod;

    public tabOptions: any[] = [];

    public submitAttempted = signal(false);
    public saving = signal(false);
    public loading = signal(false);

    public teyaConnectionStatus = toSignal(this.teyaService.checkOrFetchConnectionStatus(this.currentStore()?.id));

    public linkedBookingId = computed(() => {
        const cartLine = (this.invoicingCart()?.saleLines || []).find((line: ICartItem) => line.bookingId != null);
        if (cartLine?.bookingId) {
            return cartLine.bookingId;
        }

        const saleLine = (this.existingSaleData()?.saleLines || []).find((line: any) => line.bookingId != null);
        return saleLine?.bookingId ?? null;
    });

    public hasLinkedBooking = computed(() => !!this.linkedBookingId());
    public hasTerminal = computed(() => this.teyaConnectionStatus()?.active === true);
    public hasBukPay = computed(() => this.storesService.isSalePublicPayActive(this.currentStore()));
    public hasMbWay = computed(() => this.storesService.isStripeMbWayActive(this.currentStore()));

    public paymentChannelTabs = computed((): PaymentChannelTab[] => {
        const tabs: PaymentChannelTab[] = [
            {
                id: 'register',
                labelKey: 'INVOICE_PAYMENT_METHOD.tabRegister',
                hintKey: 'INVOICE_PAYMENT_METHOD.tabRegisterHint',
                icon: 'receipt_long',
                iconTone: 'default',
            },
        ];

        // Always expose online/terminal channels (even when inactive) so we can
        // upsell activation — same pattern as simple-payment-modal / buk-ds ChoiceTabs.
        if (this.hasPaymentAmountToCollect()) {
            if (this.intlService.getCurrentCountry() === 'pt') {
                tabs.push({
                    id: 'mbway_request',
                    labelKey: 'INVOICE_PAYMENT_METHOD.tabMbWay',
                    hintKey: 'INVOICE_PAYMENT_METHOD.tabMbWayHint',
                    logoSrc: 'assets/mbway-mark.png',
                    logoClass: 'tw:h-8 tw:w-8 tw:object-contain',
                    iconTone: 'brand',
                });
            }

            tabs.push({
                id: 'bukpay',
                labelKey: 'INVOICE_PAYMENT_METHOD.tabBukPay',
                hintKey: 'INVOICE_PAYMENT_METHOD.tabBukPayHint',
                logoSrc: 'assets/buk-icon.png',
                logoClass: 'tw:h-full tw:w-full tw:object-cover',
                iconTone: 'brand',
            });
        }

        tabs.push({
            id: 'teya',
            labelKey: 'INVOICE_PAYMENT_METHOD.tabTeya',
            hintKey: 'INVOICE_PAYMENT_METHOD.tabTeyaHint',
            logoSrc: 'assets/teya-logo.png',
            logoClass: 'tw:h-full tw:w-full tw:object-cover',
            iconTone: 'inverse',
        });

        return tabs;
    });

    /** Icon well classes — mirrors buk-ds ChoiceTabs `iconWellClass`. */
    choiceTabIconWellClass(tab: PaymentChannelTab, active: boolean): string {
        const tone = tab.iconTone || 'default';

        if (tone === 'inverse') {
            return active
                ? 'tw:bg-neutral-900 tw:ring-1 tw:ring-white/15'
                : 'tw:bg-neutral-900';
        }

        if (tone === 'brand') {
            return active ? 'tw:bg-white' : 'tw:bg-neutral-50';
        }

        return active
            ? 'tw:bg-white/20 tw:text-white'
            : 'tw:bg-primary-50 tw:text-primary-600';
    }

    /** Manual "already paid" chips — Teya / online requests live in their own tabs. */
    public paymentMethods = computed(() => {
        return this.invoicingService.paymentMethods.filter(m => !m.isOnlinePayment).filter(m => {
            const country = this.intlService.getCurrentCountry();
            if ([IPaymentMethod.MBREF, IPaymentMethod.MBWAY].includes(m.value) && country !== 'pt') {
                return false;
            }
            return true;
        });
    });

    public maxPaymentAmountCents = signal(0);

    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());

    public caeOptions = signal<ICaeOption[]>([]);

    isChargingMethodSelected = signal(false);

    // Keep salePayments controls in sync when methods resolve after form build
    private syncSalePaymentsControls = effect(() => {
        const methods = this.paymentMethods();
        const salePaymentsGroup = this.form?.get('salePayments') as FormGroup | undefined;

        methods.forEach(method => {
            if (salePaymentsGroup && !salePaymentsGroup.contains(method.value)) {
                salePaymentsGroup.addControl(method.value, this.fb.control(0));
            }
        });

        if (salePaymentsGroup && !salePaymentsGroup.contains(IPaymentMethod.TEYA)) {
            salePaymentsGroup.addControl(IPaymentMethod.TEYA, this.fb.control(0));
        }
    });

    hasPaymentAmountToCollect(): boolean {
        return (this.maxPaymentAmountCents() || 0) > 0;
    }

    showCaeSelection(): boolean {
        return !!this.currentStore()?.caeSelectionActive
            && !!this.isInvoicingActive()
            && this.canEditClientDetails();
    }

    canEditClientDetails(): boolean {
        return [CheckoutMode.CREATE_FULL_SALE, CheckoutMode.INVOICE_REMAINING].includes(this.checkoutMode());
    }

    clientVatCountry(): string {
        const merchantCountry = this.countryCodeFrom(this.currentMerchant()?.country);
        if (merchantCountry) {
            return merchantCountry;
        }

        return this.intlService.getCurrentCountry() || 'pt';
    }

    private countryCodeFrom(country: string | null | undefined): string | null {
        if (!country) {
            return null;
        }

        const raw = String(country).trim();
        if (/^[A-Za-z]{2}$/.test(raw)) {
            return raw.toLowerCase();
        }

        return this.countryService.getCountryCode(raw) || null;
    }

    clientVatInvalidMessage(): string {
        return this.form?.get('clientVatNumber')?.errors?.vatInvalid?.message
            || this.translateService.instant('INVOICE_PAYMENT_METHOD.vatInvalid');
    }

    ngOnInit(): void {
        const saleId = this.activatedRoute.snapshot?.params['saleId'];

        if (saleId) {
            const checkoutMode = this.activatedRoute.snapshot.data as { invoiceCreationMode?: CheckoutMode };

            // Editing existing sale OR adding payment to sale
            this.invoicingService.checkoutMode$.next(checkoutMode?.invoiceCreationMode || CheckoutMode.ADD_PAYMENT);
            this.loading.set(true);

            const isInvoiceRemaining = checkoutMode?.invoiceCreationMode === CheckoutMode.INVOICE_REMAINING;

            this.invoicingService.getSaleDetails(saleId, this.currentStore().id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (res) => {
                        // A paid sale can still need invoicing (e.g. payment succeeded but the
                        // invoice failed to emit), so only bounce paid sales when adding a payment.
                        if (res.status === ISaleStatus.PAID && !isInvoiceRemaining) {
                            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
                            return;
                        }
                        this.existingSaleData.set(res);
                        this.invoicingService.setPatientFromSale(res);

                        this.invoicingService.generateExistingSalePreview(res);

                        this.setupForm(res);
                    },
                    error: () => {
                        // If error (sale not found), redirect to sales page
                        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
                    },
                });

        } else {
            // New sale/payment creation flow
            // Redirect to payment creation if no items in cart
            this.invoicingService.checkoutMode$.next(CheckoutMode.CREATE_FULL_SALE);

            if (!this.invoicingCart()?.saleLines.length || !saleLinesHaveNonZeroUnitPrice(this.invoicingCart()?.saleLines)) {
                this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
                return;
            }

            this.setupForm();
        }

    }

    setupForm(data?: any) {
        // Set client edit mode and max payment amount
        if (data) {
            // existing sale payment mode
            this.clientEditMode.set(false);
            this.maxPaymentAmountCents.set((data?.totalCents - data.paidAmountCents) || 0);
        } else {
            // new sale creation mode
            this.clientEditMode.set(!this.selectedPatient());
            this.maxPaymentAmountCents.set(this.invoicingCartTotals()?.totalCents || 0);
        }

        const hasPositivePaymentAmount = this.maxPaymentAmountCents() > 0;

        this.tabOptions = [];

        if (hasPositivePaymentAmount) {
            this.tabOptions.push({ label: this.translateService.instant('INVOICE_PAYMENT_METHOD.full'), value: 'full' });
            this.tabOptions.push({ label: this.translateService.instant('INVOICE_PAYMENT_METHOD.partial'), value: 'partial' });
            if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE || (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING)) {
                this.tabOptions.push({ label: this.translateService.instant('INVOICE_PAYMENT_METHOD.later'), value: 'later' });
            }
        } else {
            this.activeTab.set('full');
        }

        const { clientName, clientVatNumber } = this.invoicingService.resolveInvoiceClientBillingDetails(
            data,
            this.invoicingService.invoicingPatient$.value,
        );

        const persistsClientVat = this.canEditClientDetails() || data?.status === ISaleStatus.DRAFT;
        const patient = this.selectedPatient() || data?.merchantClient;
        const clientVatValidators = persistsClientVat
            ? [vatCountryFormatValidator(this.clientVatCountry(), this.translateService)]
            : [];

        this.form = this.fb.group({
            clientName: [clientName || ''],
            clientVatNumber: [clientVatNumber || '', clientVatValidators],
            cae: [null],
            activePaymentMethods: [[]], // Initialize with an empty array
            partialPaymentAmount: [0],
            salePayments: this.fb.group({}),
            description: ['', Validators.maxLength(200)],
            requestEmail: [patient?.email || '', [Validators.email]],
            requestPhone: [patient?.phoneNumber || ''],
        });

        this.paymentMethods().forEach(method => {
            (this.form.get('salePayments') as FormGroup)?.addControl(method.value, this.fb.control(0));
        });
        (this.form.get('salePayments') as FormGroup)?.addControl(IPaymentMethod.TEYA, this.fb.control(0));

        this.paymentChannel.set('register');
        this.isChargingMethodSelected.set(false);

        if (isPlatformBrowser(this.platformId) && typeof Stripe === 'function') {
            this.stripe = Stripe(environment.stripe.publishableKey);
        }

        this.form.get('activePaymentMethods')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.resetFormValuesAndValidations();

                // Smooth scroll to the payment div
                setTimeout(() => {
                    if (this.paymentMethodsContainer) {
                        this.paymentMethodsContainer.nativeElement.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 20);
            });

        this.form.get('partialPaymentAmount')?.valueChanges
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                setTimeout(() => {
                    this.resetFormValuesAndValidations();
                }, 20);
            });

        this.resetFormValuesAndValidations();

        this.loadCaes();

        this.loading.set(false);
    }

    private loadCaes() {
        if (!this.showCaeSelection()) {
            return;
        }

        this.invoicingService.getCaes(this.currentStore().id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const options = (res?.caes || []).map((cae) => ({
                        ...cae,
                        label: cae.description ? `${cae.code} - ${cae.description}` : cae.code,
                    }));
                    this.caeOptions.set(options);
                    if (options.length) {
                        this.form.get('cae')?.setValue(options[0]);
                    }
                },
                // A failed lookup must never block invoicing: hide the dropdown instead.
                error: () => this.caeOptions.set([]),
            });
    }

    resetFormValuesAndValidations() {
        let totalCents = 0;
        if (this.activeTab() === 'full') {
            totalCents = this.maxPaymentAmountCents() || 0;
            this.setSalePaymentDefaultValues(totalCents);
        }

        if (this.activeTab() === 'partial') {
            totalCents = this.form.value.partialPaymentAmount ? Math.round((this.form.value.partialPaymentAmount * 100)) : 0;
            this.setSalePaymentDefaultValues(totalCents);
        }

        if (this.activeTab() === 'later') {
            this.form.get('activePaymentMethods')?.patchValue([], { emitEvent: false });
        }

        this.setPaymentMethodValidators();
        this.setPartialPaymentValidators();
        this.setSalePaymentValidators(totalCents);

        this.form.updateValueAndValidity();

    }

    setPaymentMethodValidators() {
        const totalCents = this.maxPaymentAmountCents() || 0;
        const channel = this.paymentChannel();
        if (this.activeTab() === 'later' || totalCents === 0 || channel !== 'register') {
            this.form.get('activePaymentMethods')?.setValidators(null);
        } else {
            this.form.get('activePaymentMethods')?.setValidators([Validators.required, Validators.minLength(1)]);
        }
        this.form.get('activePaymentMethods')?.updateValueAndValidity({ emitEvent: false });
        this.syncRequestContactValidators();
    }

    private syncRequestContactValidators() {
        if (!this.form) {
            return;
        }

        const email = this.form.get('requestEmail');
        const phone = this.form.get('requestPhone');
        const channel = this.paymentChannel();

        if (channel === 'bukpay' && this.activeTab() !== 'later') {
            email?.setValidators([Validators.required, Validators.email]);
            phone?.clearValidators();
        } else if (channel === 'mbway_request' && this.activeTab() !== 'later') {
            phone?.setValidators([Validators.required]);
            email?.setValidators([Validators.email]);
        } else {
            email?.setValidators([Validators.email]);
            phone?.clearValidators();
        }

        email?.updateValueAndValidity({ emitEvent: false });
        phone?.updateValueAndValidity({ emitEvent: false });
    }

    selectPaymentChannel(channel: PaymentChannel) {
        this.paymentChannel.set(channel);
        this.submitAttempted.set(false);

        if (channel === 'teya') {
            this.form?.get('activePaymentMethods')?.patchValue([IPaymentMethod.TEYA], { emitEvent: false });
            this.isChargingMethodSelected.set(true);
        } else if (channel === 'register') {
            this.form?.get('activePaymentMethods')?.patchValue([], { emitEvent: false });
            this.isChargingMethodSelected.set(false);
        } else {
            this.form?.get('activePaymentMethods')?.patchValue([], { emitEvent: false });
            this.isChargingMethodSelected.set(false);
        }

        this.resetFormValuesAndValidations();
    }

    isChannelActive(channel: PaymentChannel): boolean {
        if (channel === 'teya') {
            return this.hasTerminal();
        }
        if (channel === 'bukpay') {
            return this.hasBukPay();
        }
        if (channel === 'mbway_request') {
            return this.hasMbWay();
        }
        return true;
    }

    needsChannelActivation(): boolean {
        const channel = this.paymentChannel();
        return channel !== 'register' && !this.isChannelActive(channel);
    }

    amountDueCents(): number {
        if (this.activeTab() === 'partial') {
            return this.form?.value?.partialPaymentAmount
                ? Math.round(this.form.value.partialPaymentAmount * 100)
                : 0;
        }
        return this.maxPaymentAmountCents() || 0;
    }

    /** Selected methods on the Register tab (manual “already paid” chips). */
    selectedRegisterMethods(): IPaymentMethod[] {
        return this.form?.get('activePaymentMethods')?.value || [];
    }

    /**
     * Shared “Valor” row under every channel — mirrors buk-ds CheckoutStep.
     * Register with multiple methods keeps the editable split panel instead.
     */
    showChannelAmountSummary(): boolean {
        if (this.activeTab() === 'later') {
            return false;
        }

        if (this.paymentChannel() === 'register') {
            return this.selectedRegisterMethods().length === 1;
        }

        return true;
    }

    channelAmountLabel(): string {
        const channel = this.paymentChannel();

        if (channel === 'register') {
            const selected = this.selectedRegisterMethods()[0];
            return this.paymentMethods().find((m) => m.value === selected)?.label
                || this.translateService.instant('INVOICE_PAYMENT_METHOD.paymentMethod');
        }

        if (channel === 'mbway_request') {
            return this.translateService.instant('INVOICE_PAYMENT_METHOD.tabMbWay');
        }

        if (channel === 'bukpay') {
            return this.translateService.instant('INVOICE_PAYMENT_METHOD.tabBukPay');
        }

        return this.translateService.instant('INVOICE_PAYMENT_METHOD.teya');
    }

    submitButtonCopy(): string {
        if (this.activeTab() === 'later') {
            return this.isInvoicingActive()
                ? 'INVOICE_PAYMENT_METHOD.doInvoice'
                : 'INVOICE_PAYMENT_METHOD.doSale';
        }

        const channel = this.paymentChannel();
        if (this.needsChannelActivation()) {
            if (channel === 'bukpay') {
                return 'INVOICE_PAYMENT_METHOD.activateBukPay';
            }
            if (channel === 'mbway_request') {
                return 'INVOICE_PAYMENT_METHOD.activateMbWay';
            }
            return 'INVOICE_PAYMENT_METHOD.activateTeya';
        }

        if (channel === 'teya') {
            return 'INVOICE_PAYMENT_METHOD.continueToTerminal';
        }
        if (channel === 'bukpay') {
            return 'INVOICE_PAYMENT_METHOD.sendBukPayLink';
        }
        if (channel === 'mbway_request') {
            return 'INVOICE_PAYMENT_METHOD.sendMbWayRequest';
        }

        if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE || this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
            return this.isInvoicingActive()
                ? 'INVOICE_PAYMENT_METHOD.doInvoice'
                : 'INVOICE_PAYMENT_METHOD.doSale';
        }

        return 'GENERAL.pay';
    }

    goActivateChannel() {
        const slug = this.currentStore()?.slug;
        if (!slug) {
            return;
        }

        const channel = this.paymentChannel();
        const path = channel === 'teya' ? 'extensions/teya' : 'extensions/payments';
        this.router.navigateByUrl(`/${slug}/dashboard/${path}`);
    }

    private parsePhoneNumber(value: ChangeData | string | null): string {
        if (!value) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        return value.e164Number || value.internationalNumber || value.number || '';
    }

    setPartialPaymentValidators() {
        const maxValue = this.maxPaymentAmountCents() || 0
        if (this.activeTab() === 'partial') {
            this.form.get('partialPaymentAmount')?.setValidators([Validators.required, this.toolsService.positiveNumberValidator(), Validators.max(maxValue/100)]);
        } else {
            this.form.get('partialPaymentAmount')?.setValidators(null);
            this.form.get('partialPaymentAmount')?.patchValue(0);
        }
        this.form.get('partialPaymentAmount')?.updateValueAndValidity();
    }

    setSalePaymentDefaultValues(totalCents: number) {
        setTimeout(() => {
            const activeMethods = this.form.value.activePaymentMethods;
            if (!activeMethods || activeMethods.length === 0) return;

            const salePaymentsGroup = this.form.get('salePayments') as FormGroup;

            // Calculate sum of all but last selected payment method
            let sumOtherMethods = 0;
            for (let i = 0; i < activeMethods.length - 1; i++) {
                const method = activeMethods[i];
                const value = Number(salePaymentsGroup.get(method)?.value) || 0;
                sumOtherMethods += Math.round(value * 100);
            }

            // Remaining amount for the last method
            const remainingCents = totalCents - sumOtherMethods;
            const lastMethod = activeMethods[activeMethods.length - 1];

            // Set the last method's value in euros (cents / 100), but not negative
            salePaymentsGroup.get(lastMethod)?.patchValue(Math.max(remainingCents / 100, 0));
        });
    }

    setSalePaymentValidators(totalCents?: number) {
        const channel = this.paymentChannel();
        // Teya amount is taken from amountDueCents() on submit — only register uses salePayments UI.
        const usesSalePayments = channel === 'register';

        if (totalCents === 0 || !usesSalePayments) {
            Object.keys((this.form.get('salePayments') as FormGroup).controls).forEach(key => {
                (this.form.get('salePayments') as FormGroup).get(key)?.setValidators(null);
                if (!usesSalePayments || totalCents === 0) {
                    (this.form.get('salePayments') as FormGroup).get(key)?.patchValue(0, { emitEvent: false });
                }
                (this.form.get('salePayments') as FormGroup).get(key)?.updateValueAndValidity({ emitEvent: false });
            });
            (this.form.get('salePayments') as FormGroup).setValidators(null);
            (this.form.get('salePayments') as FormGroup).updateValueAndValidity({ emitEvent: false });
            return;
        }

        Object.keys((this.form.get('salePayments') as FormGroup).controls).forEach(key => {
            if (this.form.value.activePaymentMethods.includes(key)) {
                (this.form.get('salePayments') as FormGroup).get(key)?.setValidators([Validators.required, this.toolsService.positiveNumberValidator()]);
            } else {
                (this.form.get('salePayments') as FormGroup).get(key)?.setValidators(null);
                (this.form.get('salePayments') as FormGroup).get(key)?.updateValueAndValidity();
                (this.form.get('salePayments') as FormGroup).get(key)?.patchValue(0);
            }
        });
        if (totalCents) {
            // add validator to check if the sum of all payment methods is equal to totalCents
            (this.form.get('salePayments') as FormGroup).setValidators((control) => {
                const group = control as FormGroup;
                const sumCents = Object.keys(group.controls).reduce((acc, key) => {
                    const value = this.form.value.activePaymentMethods.includes(key) ? (Number(group.get(key)?.value) || 0) : 0;
                    return acc + Math.round(value * 100);
                }, 0);
                return sumCents === totalCents ? null : { sumMismatch: true };
            });
            (this.form.get('salePayments') as FormGroup).updateValueAndValidity();
        } else {
            (this.form.get('salePayments') as FormGroup).setValidators(null);
            (this.form.get('salePayments') as FormGroup).updateValueAndValidity();
        }
    }

    onSubmit({ createDraft = false, updateDraft = false } = {}) {
        if (this.saving()) {
            return;
        }

        this.submitAttempted.set(true);

        const finalizeDraft = this.existingSaleData()?.status === ISaleStatus.DRAFT && !createDraft && !updateDraft;
        if (this.form.get('clientVatNumber')?.invalid) {
            this.clientEditMode.set(true);
        }

        if (!createDraft && !updateDraft && this.activeTab() !== 'later' && this.needsChannelActivation()) {
            this.goActivateChannel();
            return;
        }

        if (!this.form.valid) {
            return;
        }

        if (createDraft || updateDraft) {
            this.saving.set(true);
            const payload = this.buildSalePayload({ payLater: true, salePayments: [] });
            this.runSaleRequest(payload, [], { createDraft, updateDraft });
            return;
        }

        const channel = this.paymentChannel();
        if (!createDraft && !updateDraft && this.activeTab() !== 'later' && (channel === 'bukpay' || channel === 'mbway_request')) {
            this.submitOnlineRequest(channel, finalizeDraft);
            return;
        }

        this.submitRegisterOrTeya({ createDraft, updateDraft, finalizeDraft });
    }

    saveDraft() {
        this.onSubmit({ createDraft: true });
    }

    updateDraft() {
        this.onSubmit({ updateDraft: true });
    }

    private buildSalePayload(options: {
        payLater: boolean;
        salePayments: any[];
        ephemeralCheckout?: boolean;
    }): ICreateSaleData {
        const saleLines: ICartItem[] = this.invoicingService.mapSaleLineToSave(this.invoicingCart()?.saleLines);

        return {
            storeId: this.currentStore().id,
            merchantId: this.currentMerchant().id,
            currency: this.currentMerchant()?.currency,
            merchantClientId: this.selectedPatient()?.id || null,

            clientName: this.form.value.clientName || null,
            clientVatNumber: String(this.form.value.clientVatNumber) || null,

            ...(this.showCaeSelection() ? { cae: this.form.value.cae?.code || null } : {}),

            description: this.form.value.description || '',

            payLater: options.payLater,
            partialPayment: this.activeTab() === 'partial',
            ...(options.ephemeralCheckout ? { ephemeralCheckout: true } : {}),

            discountPercentage: Number(this.invoicingCart()?.discountPercentage) || 0,
            discountAmountCents: Number(this.invoicingCart()?.discountAmountCents) || 0,

            saleLines,
            salePayments: options.salePayments,
        };
    }

    private submitRegisterOrTeya(options: { createDraft: boolean; updateDraft: boolean; finalizeDraft: boolean }) {
        this.saving.set(true);
        const shouldCreateSalePayments = (this.maxPaymentAmountCents() || 0) > 0 && this.activeTab() !== 'later';
        const activeMethods = shouldCreateSalePayments
            ? (this.paymentChannel() === 'teya' ? [IPaymentMethod.TEYA] : this.form.value.activePaymentMethods)
            : [];

        const salePayments = activeMethods.map((method: string) => {
            const amountCents = this.paymentChannel() === 'teya'
                ? this.amountDueCents()
                : Math.round((this.form.value.salePayments[method] || 0) * 100);

            return {
                paymentMethod: method,
                amountCents,
                currency: this.currentMerchant()?.currency || undefined,
            };
        });

        const isTeyaCheckout = this.paymentChannel() === 'teya'
            && this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE;

        const payload = this.buildSalePayload({
            payLater: this.activeTab() === 'later',
            salePayments,
            ephemeralCheckout: isTeyaCheckout,
        });

        this.runSaleRequest(payload, salePayments, options);
    }

    private resolveOnlineSaleId(): number | null {
        return this.invoicingService.completeInvoiceData$.value?.id
            || this.existingSaleData()?.id
            || null;
    }

    private submitOnlineRequest(channel: 'bukpay' | 'mbway_request', finalizeDraft: boolean) {
        const storeId = this.currentStore()?.id;
        const amountCents = this.amountDueCents();

        if (!storeId || amountCents < 50) {
            this.toastNotificationsService.showToast(
                this.translateService.instant('GENERAL.generalErrorMsg'),
                'error',
            );
            return;
        }

        this.saving.set(true);

        const afterSaleRegistered = () => {
            const saleId = this.resolveOnlineSaleId();
            if (!saleId) {
                this.toastNotificationsService.showToast(
                    this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
                this.saving.set(false);
                return;
            }

            if (channel === 'bukpay') {
                this.sendBukPayLink(saleId, amountCents);
            } else {
                this.sendMbWayRequest(saleId, amountCents);
            }
        };

        // ADD_PAYMENT / EDIT_SALE: sale already exists — only send the online request.
        if (!finalizeDraft && (this.checkoutMode() === CheckoutMode.ADD_PAYMENT || this.checkoutMode() === CheckoutMode.EDIT_SALE)) {
            if (!this.invoicingService.completeInvoiceData$.value?.id && this.existingSaleData()) {
                this.invoicingService.completeInvoiceData$.next(this.existingSaleData());
            }
            afterSaleRegistered();
            return;
        }

        const isEphemeralMbWay = channel === 'mbway_request'
            && this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE;

        const payload = this.buildSalePayload({
            payLater: true,
            salePayments: [],
            ephemeralCheckout: isEphemeralMbWay,
        });

        let request;
        if (finalizeDraft) {
            request = this.invoicingService.finalizeDraft(this.existingSaleData()?.id, payload);
        } else if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
            request = this.invoicingService.createSale(payload);
        } else if (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
            request = this.invoicingService.addInvoiceToExistingSale(this.existingSaleData()?.id, payload);
        } else {
            request = this.invoicingService.createPaymentForExistingSale(this.existingSaleData()?.id, payload);
        }

        request?.subscribe({
            next: (res) => {
                if (finalizeDraft) {
                    this.existingSaleData.set(res);
                }
                this.invoicingService.completeInvoiceData$.next(res);
                this.invoicingService.generateExistingSalePreview(res);
                afterSaleRegistered();
            },
            error: (error) => {
                this.toastNotificationsService.showToast(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
                this.saving.set(false);
            },
        });
    }

    private sendBukPayLink(saleId: number, amountCents: number) {
        const email = (this.form.value.requestEmail || '').trim();
        this.storesService
            .createSalePaymentLink(saleId, { amountCents, sendEmail: true, email })
            .subscribe({
                next: () => {
                    this.toastNotificationsService.showToast(
                        this.translateService.instant('INVOICE_PAYMENT_METHOD.linkSentToast'),
                        'success',
                    );
                    this.goToNextStep();
                },
                error: (error) => {
                    this.toastNotificationsService.showToast(
                        error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                        'error',
                    );
                    this.saving.set(false);
                },
            });
    }

    private sendMbWayRequest(saleId: number, amountCents: number) {
        const phoneNumber = this.parsePhoneNumber(this.form.value.requestPhone);
        this.storesService.chargeSaleMbWay(saleId, amountCents).subscribe({
            next: async (result) => {
                try {
                    const paymentIntent = await this.confirmMbWayPayment(result.clientSecret, phoneNumber);
                    const status = paymentIntent?.status;

                    if (
                        status !== 'succeeded'
                        && status !== 'requires_action'
                        && status !== 'processing'
                    ) {
                        throw new Error(
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
                        );
                    }

                    // Success page shows loading and polls until paid, then refreshes sale data.
                    this.invoicingService.pendingMbWayPayment$.next({
                        clientSecret: result.clientSecret,
                        amountCents,
                        phone: phoneNumber,
                    });
                    this.goToNextStep();
                } catch (error: any) {
                    this.toastNotificationsService.showToast(
                        error?.message || this.translateService.instant('GENERAL.generalErrorMsg'),
                        'error',
                    );
                    this.discardEphemeralSaleAfterFailedCharge(saleId);
                }
            },
            error: (error) => {
                this.toastNotificationsService.showToast(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
                this.discardEphemeralSaleAfterFailedCharge(saleId);
            },
        });
    }

    /**
     * CREATE_FULL_SALE + MB Way creates an ephemeral sale before the charge.
     * If confirm/charge fails before the success page, discard it so the user
     * can edit the cart and retry without an orphan pending sale.
     */
    private discardEphemeralSaleAfterFailedCharge(saleId: number): void {
        if (this.checkoutMode() !== CheckoutMode.CREATE_FULL_SALE) {
            this.saving.set(false);
            return;
        }

        this.invoicingService.discardEphemeralCheckoutSale(saleId).subscribe({
            next: () => {
                this.invoicingService.clearCompleteInvoiceData();
                this.invoicingService.clearExistingSalePreview();
                this.saving.set(false);
            },
            error: () => {
                this.saving.set(false);
            },
        });
    }

    private async confirmMbWayPayment(
        clientSecret: string,
        phoneNumber: string,
    ): Promise<stripe.paymentIntents.PaymentIntent | undefined> {
        if (!isPlatformBrowser(this.platformId)) {
            throw new Error(this.translateService.instant('GENERAL.generalErrorMsg'));
        }

        if (!this.stripe) {
            if (typeof Stripe !== 'function') {
                throw new Error(this.translateService.instant('GENERAL.generalErrorMsg'));
            }
            this.stripe = Stripe(environment.stripe.publishableKey);
        }

        // handleActions: false returns when the MB Way push is sent (requires_action).
        // The success page polls until the customer approves/declines.
        const result = await this.stripe.confirmMbWayPayment(
            clientSecret,
            {
                payment_method: {
                    billing_details: {
                        phone: phoneNumber,
                        email: this.form.value.requestEmail || undefined,
                    },
                },
            },
            { handleActions: false },
        );

        if (result.error) {
            throw new Error(result.error.message);
        }

        return result.paymentIntent;
    }

    private runSaleRequest(
        payload: ICreateSaleData,
        salePayments: any[],
        options: { createDraft?: boolean; updateDraft?: boolean; finalizeDraft?: boolean } = {},
    ) {
        const { createDraft = false, updateDraft = false, finalizeDraft = false } = options;
        let request;
        if (createDraft) {
            request = this.invoicingService.createDraft(payload);
        } else if (updateDraft) {
            request = this.invoicingService.updateDraft(this.existingSaleData()?.id, payload);
        } else if (finalizeDraft) {
            request = this.invoicingService.finalizeDraft(this.existingSaleData()?.id, payload);
        } else if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
            request = this.invoicingService.createSale(payload);
        } else if (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
            request = this.invoicingService.addInvoiceToExistingSale(this.existingSaleData()?.id, payload);
        } else {
            request = this.invoicingService.createPaymentForExistingSale(this.existingSaleData()?.id, payload);
        }

        request?.subscribe({
            next: (res) => {
                if (createDraft || updateDraft) {
                    // Drafts are not completed sales: skip the Teya / success steps
                    this.saving.set(false);
                    this.toastNotificationsService.showToast(this.translateService.instant('GENERAL.savedSuccessfully'), 'success');
                    this.invoicingService.clearCart();
                    this.invoicingService.setPatient(null);
                    this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
                    return;
                }

                this.invoicingService.completeInvoiceData$.next(res);

                const teyaUnchargedAmount = salePayments?.find(payment => payment.paymentMethod === IPaymentMethod.TEYA)?.amountCents || 0;
                this.invoicingService.unchargedTeyaAmount$.next(teyaUnchargedAmount);

                this.goToNextStep();
                this.invoicingService.generateExistingSalePreview(res);
            },
            error: (error) => {
                this.toastNotificationsService.showToast(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
                this.saving.set(false);
            },
        });
    }

    onPaymentTabSelected(_tab: string) {
        if (this.activeTab() === 'later') {
            this.paymentChannel.set('register');
            this.isChargingMethodSelected.set(false);
        }
        this.resetFormValuesAndValidations();
    }

    goToNextStep() {
        if (this.isChargingMethodSelected() || this.paymentChannel() === 'teya') {
            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/teya');
        } else if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/success');
        } else {
            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/payment-success');
        }
    }

    goBack() {
        if (this.saving()) {
            return;
        }

        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
    }

}
